import { naturalCompare, PLAYER_NOTE_TYPES } from '@kartothek/shared';
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError } from '../api';
import { useCampaign, useCampaignId, useInvalidateCampaign, useNoteIndex, useTypes } from '../hooks';
import { t } from '../i18n';

interface OpenOptions {
  title?: string;
  folder?: string;
  typeKey?: string;
  onCreated?: (id: string) => void;
}

interface NewNoteApi {
  open: (opts?: OpenOptions) => void;
}

const NewNoteCtx = createContext<NewNoteApi>({ open: () => {} });

export function useNewNote(): NewNoteApi {
  return useContext(NewNoteCtx);
}

/** Stellt den „Neue Notiz“-Dialog innerhalb einer Kampagne bereit. */
export function NewNoteProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<OpenOptions | null>(null);
  const value = useMemo(() => ({ open: (o: OpenOptions = {}) => setOpts(o) }), []);
  return (
    <NewNoteCtx.Provider value={value}>
      {children}
      {opts && <NewNoteDialog opts={opts} onClose={() => setOpts(null)} />}
    </NewNoteCtx.Provider>
  );
}

function guessType(folder: string, index: { folder: string; typeKey: string }[]): string {
  const counts = new Map<string, number>();
  for (const n of index) if (n.folder === folder) counts.set(n.typeKey, (counts.get(n.typeKey) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'notiz';
}

function NewNoteDialog({ opts, onClose }: { opts: OpenOptions; onClose: () => void }) {
  const cid = useCampaignId();
  const index = useNoteIndex(cid);
  const types = useTypes(cid);
  const invalidate = useInvalidateCampaign(cid);
  const [title, setTitle] = useState(opts.title ?? '');
  const [folder, setFolder] = useState(opts.folder ?? '');
  const [typeKey, setTypeKey] = useState(opts.typeKey ?? guessType(opts.folder ?? '', index.data ?? []));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const campaign = useCampaign(cid);
  // Spieler: eigener Ordner (legt der Server fest), nur Notiz oder Charakter
  const player = campaign.data?.role === 'player';
  const typeOptions = (types.data ?? []).filter((ty) => !player || (PLAYER_NOTE_TYPES as readonly string[]).includes(ty.key));

  const folders = useMemo(
    () => [...new Set((index.data ?? []).map((n) => n.folder).filter(Boolean))].sort(naturalCompare),
    [index.data],
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const note = await api.createNote(cid, player ? { title, typeKey: (PLAYER_NOTE_TYPES as readonly string[]).includes(typeKey) ? typeKey : 'notiz' } : { title, folder, typeKey });
      invalidate();
      onClose();
      opts.onCreated?.(note.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <form className="modal" onMouseDown={(e) => e.stopPropagation()} onSubmit={submit}>
        <h2>{t('note.createTitle')}</h2>
        <label>
          {t('note.title')}
          <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={200} />
        </label>
        {!player && (
          <label>
            {t('note.folder')}
            <input
              list="folder-list"
              value={folder}
              onChange={(e) => {
                setFolder(e.target.value);
                setTypeKey(guessType(e.target.value, index.data ?? []));
              }}
              placeholder={t('nav.root')}
            />
            <datalist id="folder-list">
              {folders.map((f) => (
                <option key={f} value={f} />
              ))}
            </datalist>
          </label>
        )}
        {player && <p className="small muted">{t('note.playerNoteHint')}</p>}
        <label>
          {t('note.type')}
          <select value={typeKey} onChange={(e) => setTypeKey(e.target.value)}>
            {typeOptions.map((ty) => (
              <option key={ty.key} value={ty.key}>
                {ty.label}
              </option>
            ))}
          </select>
        </label>
        {error && <div className="error">{error}</div>}
        <div className="modal-actions">
          <button type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="primary" disabled={busy || !title.trim()}>
            {t('common.create')}
          </button>
        </div>
      </form>
    </div>
  );
}
